import { useEffect, useMemo, useState, type ReactNode } from "react";

export const PAGE_SIZES = [10, 20, 25, 50, 100, 0] as const; // 0 = all

const load = (k: string, d: number) => {
  try { const raw = localStorage.getItem(`rca:pagesize:${k}`); if (raw == null || raw === "") return d; const v = Number(raw); return PAGE_SIZES.includes(v as any) ? v : d; } catch { return d; }
};
const save = (k: string, v: number) => { try { localStorage.setItem(`rca:pagesize:${k}`, String(v)); } catch { /* private mode */ } };

/** Client-side paging. Page size is remembered per list (per browser). */
export function usePaged<T>(rows: T[], key: string, defaultSize = 25) {
  const [size, setSizeState] = useState(() => load(key, defaultSize));
  const [page, setPage] = useState(1);
  const pages = size === 0 ? 1 : Math.max(1, Math.ceil(rows.length / size));
  useEffect(() => { if (page > pages) setPage(pages); }, [pages, page]);
  const view = useMemo(() => (size === 0 ? rows : rows.slice((page - 1) * size, page * size)), [rows, size, page]);
  const setSize = (n: number) => { setSizeState(n); save(key, n); setPage(1); };
  return { view, page, pages, size, setPage, setSize, total: rows.length, from: rows.length ? (size === 0 ? 1 : (page - 1) * size + 1) : 0, to: size === 0 ? rows.length : Math.min(rows.length, page * size) };
}

export function Pager({ p, noun = "rows" }: { p: ReturnType<typeof usePaged<any>>; noun?: string }) {
  if (p.total === 0) return null;
  const nums: (number | "…")[] = [];
  for (let i = 1; i <= p.pages; i++) {
    if (i === 1 || i === p.pages || Math.abs(i - p.page) <= 1) nums.push(i);
    else if (nums[nums.length - 1] !== "…") nums.push("…");
  }
  return (
    <div className="pager">
      <span className="pg-info">{p.from}–{p.to} of {p.total} {noun}</span>
      <label className="pg-size">Show
        <select className="select" value={p.size} onChange={(e) => p.setSize(Number(e.target.value))}>
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n === 0 ? "All" : n}</option>)}
        </select>
      </label>
      {p.pages > 1 && (
        <div className="pg-nav" role="navigation" aria-label="Pages">
          <button className="icon-btn" disabled={p.page === 1} onClick={() => p.setPage(p.page - 1)} aria-label="Previous page">‹</button>
          {nums.map((n, i) => n === "…" ? <span key={`e${i}`} className="pg-gap">…</span> :
            <button key={n} className="pg-num" aria-current={n === p.page ? "page" : undefined} onClick={() => p.setPage(n)}>{n}</button>)}
          <button className="icon-btn" disabled={p.page === p.pages} onClick={() => p.setPage(p.page + 1)} aria-label="Next page">›</button>
        </div>
      )}
    </div>
  );
}

export type SortState<K extends string> = { key: K; dir: "asc" | "desc" };

/** Sort rows by a named accessor. Nulls always go last. */
export function useSorted<T, G extends Record<string, (r: T) => string | number | null | undefined>>(rows: T[], get: G, initial: SortState<Extract<keyof G, string>>) {
  type K = Extract<keyof G, string>;
  const [sort, setSort] = useState<SortState<K>>(initial);
  const sorted = useMemo(() => {
    const f = get[sort.key];
    return [...rows].sort((a, b) => {
      const x = f(a), y = f(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
      return sort.dir === "asc" ? c : -c;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);
  const toggle = (key: K, firstDir: "asc" | "desc" = "asc") =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: firstDir }));
  return { sorted, sort, setSort, toggle };
}

/** Clickable column heading with an arrow showing the current order. */
export function SortTh<K extends string>({ k, sort, onSort, children, className, firstDir, style }: {
  k: K; sort: SortState<K>; onSort: (k: K, firstDir?: "asc" | "desc") => void; children: ReactNode; className?: string; firstDir?: "asc" | "desc"; style?: React.CSSProperties;
}) {
  const active = sort.key === k;
  return (
    <th className={className} style={style} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button className={`sort-th${active ? " on" : ""}`} onClick={() => onSort(k, firstDir)}>
        {children}<span className="arr" aria-hidden="true">{active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}
